/**
 * SaveAsTemplateDialog
 *
 * Dialog for saving the current job as a reusable template.
 * Appears in JobDetail header actions.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useJobTemplates } from '@/hooks/useJobTemplates';
import { Loader2, Bookmark } from 'lucide-react';

interface Job {
  id: string;
  title: string;
  description?: string;
  permitType?: string | null;
  trade?: string;
  jobScope?: string | null;
  estimatedValue?: number;
  isOwnerBuilder?: boolean;
}

interface SaveAsTemplateDialogProps {
  job: Job;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SaveAsTemplateDialog({
  job,
  open,
  onOpenChange,
}: SaveAsTemplateDialogProps) {
  const { toast } = useToast();
  const { saveJobAsTemplate } = useJobTemplates();

  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Generate default name from job title
  const defaultName = job.title
    ? job.title.replace(/\s*-\s*\d+.*$/, '').trim() // Remove address/number suffix
    : 'New Template';

  const handleSave = async () => {
    const name = templateName.trim() || defaultName;

    if (!name) {
      toast({
        title: 'Name Required',
        description: 'Please enter a name for the template.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      await saveJobAsTemplate(job, name, description.trim() || undefined);

      toast({
        title: 'Template Saved',
        description: `"${name}" has been saved to your templates.`,
      });

      // Reset and close
      setTemplateName('');
      setDescription('');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save template:', error);
      toast({
        title: 'Error',
        description: 'Failed to save template. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setTemplateName('');
      setDescription('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="h-5 w-5" />
            Save as Template
          </DialogTitle>
          <DialogDescription>
            Create a reusable template from this job. Templates help you quickly
            create new jobs with similar settings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Template Name</Label>
            <Input
              id="template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder={defaultName}
              disabled={isSaving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-description">
              Description <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe when to use this template..."
              rows={3}
              disabled={isSaving}
            />
          </div>

          {/* Preview of what will be saved */}
          <div className="bg-muted rounded-lg p-3 text-sm space-y-1">
            <p className="font-medium text-muted-foreground">Template will include:</p>
            <ul className="text-muted-foreground space-y-0.5">
              {job.permitType && <li>• Permit type: {job.permitType}</li>}
              {job.trade && <li>• Trade: {job.trade}</li>}
              {job.jobScope && <li>• Scope: {job.jobScope}</li>}
              {job.estimatedValue && (
                <li>• Value range: ${Math.floor(job.estimatedValue * 0.8).toLocaleString()} - ${Math.ceil(job.estimatedValue * 1.2).toLocaleString()}</li>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Template'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
